import type { ReactNode } from "react";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import { RiskLevelBadge } from "@/components/ui/risk-level-badge/RiskLevelBadge";
import type { TargetareUniversRand } from "@/types/targetare";
import {
  formatDimensiune,
  formatNumar,
  formatRon,
} from "@/utils/targetareFormatting";
import { riskLevelDinNivel } from "@/utils/targetareRisc";
import { etichetaSursa } from "@/utils/targetareSurse";
import {
  ETICHETE_ACTIONARIAT,
  ETICHETE_ORIGINE,
  type ColoanaTabel,
} from "@/utils/targetareTabel";
import SursaBadge from "../SursaBadge";
import { TierBadge } from "../TierBadge";

const MONO = new Set([
  "cui",
  "email_principal",
  "telefon_principal",
  "site",
  "email_persoana",
]);

function ScoruriServicii({
  scoruri,
}: {
  scoruri: TargetareUniversRand["servicii_scor"];
}) {
  if (!scoruri) return <span className="text-xs text-muted-foreground">–</span>;
  const lista = Object.entries(scoruri).sort((a, b) => b[1].scor - a[1].scor);
  const text = lista
    .map(([id, s]) => `${id}: ${s.scor}${s.selectat ? " ✓" : ""}`)
    .join(" · ");
  const titlu = lista
    .map(
      ([id, s]) =>
        `${id}: scor ${s.scor}${s.selectat ? " (selectat)" : ""} · CAEN ${s.caen || "–"} · listă ${s.lista || "–"}${s.coduri.length ? ` · coduri ${s.coduri.join(", ")}` : ""}${s.rezultat ? ` · ${s.rezultat}` : ""}`
    )
    .join("\n");
  return (
    <span className="block truncate font-mono text-xs" title={titlu}>
      {text}
    </span>
  );
}

function RiscSectoare({
  detalii,
}: {
  detalii: TargetareUniversRand["risc_detalii"];
}) {
  if (!detalii) return <span className="text-xs text-muted-foreground">–</span>;
  const lista = Object.entries(detalii.sectoare).sort((a, b) => b[1] - a[1]);
  return (
    <span
      className="block truncate text-xs"
      title={`mărime ${detalii.marime}, agregare ${detalii.agregare}`}
    >
      {lista
        .map(([id, s], i) => `${i === 0 ? "▲ " : ""}${id}: ${s}`)
        .join(" · ")}
    </span>
  );
}

export function celulaFirma(
  coloana: ColoanaTabel<TargetareUniversRand>,
  rand: TargetareUniversRand
): ReactNode {
  const v = rand[coloana.id];
  const sursaRand = coloana.sursaId ? rand[coloana.sursaId] : null;
  const badge =
    v && sursaRand ? <SursaBadge sursa={String(sursaRand)} /> : null;
  if (coloana.id.endsWith("_sursa")) {
    return (
      <span className="text-xs text-muted-foreground">
        {etichetaSursa(v as string | null)}
      </span>
    );
  }
  switch (coloana.id) {
    case "servicii_scor":
      return <ScoruriServicii scoruri={rand.servicii_scor} />;
    case "risc_detalii":
      return <RiscSectoare detalii={rand.risc_detalii} />;
    case "tier":
      return <TierBadge tier={rand.tier} />;
    case "clasificare_estimata":
      return (
        <ClassificationBadge
          classification={rand.clasificare_estimata}
          size="sm"
        />
      );
    case "dimensiune_stricta":
      return formatDimensiune(rand.dimensiune_stricta);
    case "actionariat":
      return ETICHETE_ACTIONARIAT[rand.actionariat] ?? rand.actionariat;
    case "origine":
      return ETICHETE_ORIGINE[String(rand.origine ?? "")] ?? rand.origine;
    case "nivel_risc":
      return rand.nivel_risc ? (
        <RiskLevelBadge riskLevel={riskLevelDinNivel(rand.nivel_risc)} />
      ) : (
        ""
      );
    case "surse":
      return (
        <span className="flex flex-wrap gap-1">
          {(rand.surse ?? []).map((s) => (
            <SursaBadge key={s} sursa={s} />
          ))}
        </span>
      );
    case "cifra_afaceri":
    case "active_totale":
      return (
        <span className="tabular-nums">{formatRon(v as number | null)}</span>
      );
    case "salariati":
    case "emailuri_valide":
    case "scor":
    case "scor_risc":
    case "nr_persoane":
      return (
        <span className="tabular-nums">{formatNumar(v as number | null)}</span>
      );
    default:
      break;
  }
  if (coloana.tip === "bool") return v ? "da" : "nu";
  if (Array.isArray(v)) {
    return (
      <span title={v.join(", ")} className="block truncate">
        {v.join(", ")}
      </span>
    );
  }
  if (MONO.has(coloana.id)) {
    return (
      <span className="inline-flex max-w-full items-center gap-1">
        <span className="truncate font-mono text-xs">{String(v ?? "")}</span>
        {badge}
      </span>
    );
  }
  return (
    <span
      title={String(v ?? "")}
      className="flex max-w-full items-center gap-1"
    >
      <span className="truncate">{String(v ?? "")}</span>
      {badge}
    </span>
  );
}
